import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import Logo from "../components/Logo";
import { toast, Toaster } from "sonner";

const TABS = [
  { key: "result", label: "Manage Result" },
  { key: "common", label: "Common Number" },
  { key: "previous", label: "Previous Result" },
  { key: "password", label: "Change Password" },
];

function todayStr() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// ────────────────────────────── Manage Result ──────────────────────────────
function ManageResult() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    city: "Shillong Night Teer",
    date: todayStr(),
    fr: "",
    sr: "",
    fr_time: "10:00 PM",
    sr_time: "11:00 PM",
    text: "",
    published: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/results");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditingId(null);
    setForm({
      city: "Shillong Night Teer",
      date: todayStr(),
      fr: "",
      sr: "",
      fr_time: "10:00 PM",
      sr_time: "11:00 PM",
      text: "",
      published: true,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await api.put(`/results/${editingId}`, form);
        toast.success("Result updated");
      } else {
        await api.post("/results", form);
        toast.success("Result added");
      }
      reset();
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this result?")) return;
    await api.delete(`/results/${id}`);
    toast.success("Deleted");
    load();
  };

  const togglePublish = async (item) => {
    await api.put(`/results/${item.id}`, { ...item, published: !item.published });
    load();
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      city: item.city,
      date: item.date,
      fr: item.fr || "",
      sr: item.sr || "",
      fr_time: item.fr_time || "10:00 PM",
      sr_time: item.sr_time || "11:00 PM",
      text: item.text || "",
      published: !!item.published,
    });
  };

  return (
    <div className="space-y-6" data-testid="manage-result">
      <form
        onSubmit={submit}
        className="bg-white rounded-xl p-4 sm:p-6 shadow border grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <div className="sm:col-span-2 font-display text-lg">
          {editingId ? "Edit Result" : "Add New Result"}
        </div>
        <Field label="City">
          <input
            className="nt-input"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            data-testid="result-city"
          />
        </Field>
        <Field label="Date (DD/MM/YYYY)">
          <input
            className="nt-input"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="result-date"
          />
        </Field>
        <Field label="F/R Number">
          <input
            className="nt-input"
            value={form.fr}
            onChange={(e) => setForm({ ...form, fr: e.target.value })}
            data-testid="result-fr"
          />
        </Field>
        <Field label="S/R Number">
          <input
            className="nt-input"
            value={form.sr}
            onChange={(e) => setForm({ ...form, sr: e.target.value })}
            data-testid="result-sr"
          />
        </Field>
        <Field label="F/R Time">
          <input
            className="nt-input"
            value={form.fr_time}
            onChange={(e) => setForm({ ...form, fr_time: e.target.value })}
          />
        </Field>
        <Field label="S/R Time">
          <input
            className="nt-input"
            value={form.sr_time}
            onChange={(e) => setForm({ ...form, sr_time: e.target.value })}
          />
        </Field>
        <Field label="Text / Note (optional)" full>
          <textarea
            rows={2}
            className="nt-input"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            data-testid="result-text"
          />
        </Field>
        <label className="flex items-center gap-2 font-bold">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => setForm({ ...form, published: e.target.checked })}
          />{" "}
          Published
        </label>
        <div className="flex gap-2 sm:col-span-2">
          <button
            className="nt-btn nt-btn-ink"
            disabled={loading}
            data-testid="result-save"
          >
            {editingId ? "Save Changes" : "Add Result"}
          </button>
          {editingId && (
            <button type="button" className="nt-btn nt-btn-red" onClick={reset}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="nt-table">
          <thead>
            <tr>
              <th>City</th>
              <th>Date</th>
              <th>F/R</th>
              <th>S/R</th>
              <th>Text</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-4 text-slate-500">
                  No results yet.
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr key={it.id}>
                  <td>{it.city}</td>
                  <td>{it.date}</td>
                  <td>{it.fr || "-"}</td>
                  <td>{it.sr || "-"}</td>
                  <td className="text-xs">{it.text || "-"}</td>
                  <td>
                    <button
                      onClick={() => togglePublish(it)}
                      className={
                        "px-2 py-1 rounded text-white text-xs " +
                        (it.published ? "bg-emerald-600" : "bg-slate-500")
                      }
                      data-testid={`toggle-${it.id}`}
                    >
                      {it.published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td>
                    <div className="flex justify-center gap-1">
                      <button
                        className="text-blue-600 font-bold"
                        onClick={() => startEdit(it)}
                      >
                        Edit
                      </button>
                      <span>·</span>
                      <button
                        className="text-red-600 font-bold"
                        onClick={() => remove(it.id)}
                        data-testid={`del-${it.id}`}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────────── Common Number ──────────────────────────────
function ManageCommon() {
  const [items, setItems] = useState([]);
  const empty = {
    date: todayStr(),
    direct1: "",
    house1: "",
    ending1: "",
    direct2: "",
    house2: "",
    ending2: "",
    text: "",
    published: true,
  };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/commons");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditingId(null);
    setForm(empty);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/commons/${editingId}`, form);
        toast.success("Common updated");
      } else {
        await api.post("/commons", form);
        toast.success("Common added");
      }
      reset();
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this common number?")) return;
    await api.delete(`/commons/${id}`);
    load();
  };
  const togglePublish = async (item) => {
    await api.put(`/commons/${item.id}`, { ...item, published: !item.published });
    load();
  };

  return (
    <div className="space-y-6" data-testid="manage-common">
      <form
        onSubmit={submit}
        className="bg-white rounded-xl p-4 sm:p-6 shadow border grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        <div className="col-span-2 sm:col-span-4 font-display text-lg">
          {editingId ? "Edit Common Number" : "Add Common Number"}
        </div>
        <Field label="Date">
          <input
            className="nt-input"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
        </Field>
        <div className="col-span-2 sm:col-span-4 text-sm font-bold text-slate-600 mt-2">
          Row 1
        </div>
        <Field label="Direct">
          <input
            className="nt-input"
            value={form.direct1}
            onChange={(e) => setForm({ ...form, direct1: e.target.value })}
          />
        </Field>
        <Field label="House">
          <input
            className="nt-input"
            value={form.house1}
            onChange={(e) => setForm({ ...form, house1: e.target.value })}
          />
        </Field>
        <Field label="Ending">
          <input
            className="nt-input"
            value={form.ending1}
            onChange={(e) => setForm({ ...form, ending1: e.target.value })}
          />
        </Field>
        <div className="col-span-2 sm:col-span-4 text-sm font-bold text-slate-600 mt-2">
          Row 2
        </div>
        <Field label="Direct">
          <input
            className="nt-input"
            value={form.direct2}
            onChange={(e) => setForm({ ...form, direct2: e.target.value })}
          />
        </Field>
        <Field label="House">
          <input
            className="nt-input"
            value={form.house2}
            onChange={(e) => setForm({ ...form, house2: e.target.value })}
          />
        </Field>
        <Field label="Ending">
          <input
            className="nt-input"
            value={form.ending2}
            onChange={(e) => setForm({ ...form, ending2: e.target.value })}
          />
        </Field>
        <Field label="Text (optional)" full>
          <textarea
            rows={2}
            className="nt-input"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
          />
        </Field>
        <label className="col-span-2 sm:col-span-4 flex items-center gap-2 font-bold">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => setForm({ ...form, published: e.target.checked })}
          />{" "}
          Published
        </label>
        <div className="col-span-2 sm:col-span-4 flex gap-2">
          <button className="nt-btn nt-btn-ink" data-testid="common-save">
            {editingId ? "Save Changes" : "Add"}
          </button>
          {editingId && (
            <button type="button" className="nt-btn nt-btn-red" onClick={reset}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="nt-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Row 1 (D / H / E)</th>
              <th>Row 2 (D / H / E)</th>
              <th>Text</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-4 text-slate-500">
                  Empty
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr key={it.id}>
                  <td>{it.date}</td>
                  <td>
                    {it.direct1 || "-"} / {it.house1 || "-"} / {it.ending1 || "-"}
                  </td>
                  <td>
                    {it.direct2 || "-"} / {it.house2 || "-"} / {it.ending2 || "-"}
                  </td>
                  <td className="text-xs">{it.text || "-"}</td>
                  <td>
                    <button
                      onClick={() => togglePublish(it)}
                      className={
                        "px-2 py-1 rounded text-white text-xs " +
                        (it.published ? "bg-emerald-600" : "bg-slate-500")
                      }
                    >
                      {it.published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td>
                    <div className="flex justify-center gap-1">
                      <button
                        className="text-blue-600 font-bold"
                        onClick={() => {
                          setEditingId(it.id);
                          setForm({
                            date: it.date,
                            direct1: it.direct1 || "",
                            house1: it.house1 || "",
                            ending1: it.ending1 || "",
                            direct2: it.direct2 || "",
                            house2: it.house2 || "",
                            ending2: it.ending2 || "",
                            text: it.text || "",
                            published: !!it.published,
                          });
                        }}
                      >
                        Edit
                      </button>
                      <span>·</span>
                      <button
                        className="text-red-600 font-bold"
                        onClick={() => remove(it.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────────── Previous Result ──────────────────────────────
function ManagePrevious() {
  const [items, setItems] = useState([]);
  const empty = {
    city: "SHILLONG",
    date: todayStr(),
    fr: "",
    sr: "",
    text: "",
    published: true,
  };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/previous");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setEditingId(null);
    setForm(empty);
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/previous/${editingId}`, form);
      else await api.post("/previous", form);
      toast.success("Saved");
      reset();
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/previous/${id}`);
    load();
  };
  const togglePublish = async (item) => {
    await api.put(`/previous/${item.id}`, { ...item, published: !item.published });
    load();
  };

  return (
    <div className="space-y-6" data-testid="manage-previous">
      <form
        onSubmit={submit}
        className="bg-white rounded-xl p-4 sm:p-6 shadow border grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        <div className="col-span-2 sm:col-span-4 font-display text-lg">
          {editingId ? "Edit Previous" : "Add Previous Result"}
        </div>
        <Field label="City">
          <input
            className="nt-input"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            data-testid="prev-city"
          />
        </Field>
        <Field label="Date (DD/MM/YYYY)">
          <input
            className="nt-input"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="prev-date"
          />
        </Field>
        <Field label="F/R">
          <input
            className="nt-input"
            value={form.fr}
            onChange={(e) => setForm({ ...form, fr: e.target.value })}
            data-testid="prev-fr"
          />
        </Field>
        <Field label="S/R">
          <input
            className="nt-input"
            value={form.sr}
            onChange={(e) => setForm({ ...form, sr: e.target.value })}
            data-testid="prev-sr"
          />
        </Field>
        <Field label="Text (optional)" full>
          <textarea
            rows={2}
            className="nt-input"
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
          />
        </Field>
        <label className="col-span-2 sm:col-span-4 flex items-center gap-2 font-bold">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => setForm({ ...form, published: e.target.checked })}
          />{" "}
          Published
        </label>
        <div className="col-span-2 sm:col-span-4 flex gap-2">
          <button className="nt-btn nt-btn-ink" data-testid="prev-save">
            {editingId ? "Save Changes" : "Add"}
          </button>
          {editingId && (
            <button type="button" className="nt-btn nt-btn-red" onClick={reset}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="nt-table">
          <thead>
            <tr>
              <th>City</th>
              <th>Date</th>
              <th>F/R</th>
              <th>S/R</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-4 text-slate-500">
                  Empty
                </td>
              </tr>
            ) : (
              items.map((it) => (
                <tr key={it.id}>
                  <td>{it.city}</td>
                  <td>{it.date}</td>
                  <td>{it.fr || "-"}</td>
                  <td>{it.sr || "-"}</td>
                  <td>
                    <button
                      onClick={() => togglePublish(it)}
                      className={
                        "px-2 py-1 rounded text-white text-xs " +
                        (it.published ? "bg-emerald-600" : "bg-slate-500")
                      }
                    >
                      {it.published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td>
                    <div className="flex justify-center gap-1">
                      <button
                        className="text-blue-600 font-bold"
                        onClick={() => {
                          setEditingId(it.id);
                          setForm({
                            city: it.city,
                            date: it.date,
                            fr: it.fr || "",
                            sr: it.sr || "",
                            text: it.text || "",
                            published: !!it.published,
                          });
                        }}
                      >
                        Edit
                      </button>
                      <span>·</span>
                      <button
                        className="text-red-600 font-bold"
                        onClick={() => remove(it.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────────── Change Password ──────────────────────────────
function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed successfully");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="max-w-md bg-white rounded-xl p-6 shadow border"
      data-testid="change-password"
    >
      <div className="font-display text-lg mb-3">Change Password</div>
      <Field label="Current Password">
        <input
          type="password"
          className="nt-input"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          data-testid="cp-current"
          required
        />
      </Field>
      <Field label="New Password">
        <input
          type="password"
          className="nt-input"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          data-testid="cp-new"
          required
          minLength={4}
        />
      </Field>
      <Field label="Confirm New Password">
        <input
          type="password"
          className="nt-input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid="cp-confirm"
          required
          minLength={4}
        />
      </Field>
      <button
        className="nt-btn nt-btn-ink w-full mt-2"
        disabled={busy}
        data-testid="cp-submit"
      >
        {busy ? "Saving…" : "Update Password"}
      </button>
    </form>
  );
}

// ────────────────────────────── Helper ──────────────────────────────
function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2 sm:col-span-4" : ""}>
      <label className="block text-xs font-bold text-slate-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

// ────────────────────────────── Layout ──────────────────────────────
export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("result");

  if (checking) {
    return <div className="p-8 text-center text-slate-600">Checking session…</div>;
  }
  if (!user || user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-right" />
      {/* Inline styles for nt-input */}
      <style>{`.nt-input{width:100%;border:2px solid #cbd5e1;border-radius:8px;padding:8px 10px;font-weight:600}.nt-input:focus{outline:none;border-color:#10b981}`}</style>

      <header className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div>
              <div className="font-display text-lg leading-none">Night Teer Admin</div>
              <div className="text-xs text-slate-500">
                Signed in as <b>{user.username}</b>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav("/")}
              className="text-sm font-bold text-slate-700 hover:text-black"
              data-testid="view-site"
            >
              View Site
            </button>
            <button
              onClick={logout}
              className="nt-btn nt-btn-red"
              data-testid="admin-logout"
            >
              Logout
            </button>
          </div>
        </div>
        <nav className="max-w-6xl mx-auto px-2 sm:px-4 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={
                "px-4 py-2 rounded-t-lg font-bold whitespace-nowrap border-b-2 " +
                (tab === t.key
                  ? "border-emerald-600 text-emerald-700"
                  : "border-transparent text-slate-600 hover:text-black")
              }
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto p-3 sm:p-6">
        {tab === "result" && <ManageResult />}
        {tab === "common" && <ManageCommon />}
        {tab === "previous" && <ManagePrevious />}
        {tab === "password" && <ChangePassword />}
      </main>
    </div>
  );
}
